import { featureOfMaster } from '@/features/masters';
import type { SidebarMenuItem } from '../types';

/**
 * The masters group's own id, and the prefix its children carry.
 *
 * Each child is `masters-<key>`, where the key is the one `MASTER_FEATURES` maps to an `os_feature`
 * name — so the id already says which feature the entry leads to and nothing has to be repeated here.
 */
const MASTERS_ID = 'master';
const CHILD_PREFIX = 'masters-';

/**
 * Drops the masters a role may not read.
 *
 * Only the masters group is touched. Its children are the menu entries that map onto features the RBAC
 * matrix governs; everything else in the sidebar — the dashboard, RFQs, templates, RBAC itself — has no
 * matrix item to test against and is returned as it was.
 *
 * A child whose key maps to no feature is kept. That is the harmless direction: the entry shows and the
 * platform still refuses anything the role may not do.
 *
 * If every child is dropped the group goes with them, rather than leaving a Master menu that opens onto
 * nothing.
 */
export function withReadableMasters(
  items: SidebarMenuItem[],
  canReach: (feature: string) => boolean,
): SidebarMenuItem[] {
  return items.flatMap((item) => {
    if (item.id !== MASTERS_ID || !item.children) return [item];

    const children = item.children.filter((child) => {
      if (!child.id.startsWith(CHILD_PREFIX)) return true;

      /* An entry that names its own feature -- Purchase Requests, a screen rather than a master -- is gated by it. */
      const feature = child.feature ?? featureOfMaster(child.id.slice(CHILD_PREFIX.length));
      return feature === undefined || canReach(feature);
    });

    return children.length === 0 ? [] : [{ ...item, children }];
  });
}
